import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { useAppLoading } from '@/stores/app/loading';
import { getQuarter } from '@/helpers/date';
import { fetchHiringReport } from '@/api/insights';
import { format } from 'date-fns';
import { useAppSnackbar } from '@/stores/app/snackbar';
import { isEmpty } from 'lodash-es';
import { numberWithCommas, roundFloat } from '@/helpers/formatters';
import { assignInsightsTableToCSVDownloadLink } from '@/helpers/fileDownload';
import { useDatasetCounts } from './datasetCounts';

export const useHiringReport = defineStore('hiringReport', () => {
  const data = ref({
    current_open_offers: 0,
    gender: {
      male: 0,
      female: 0,
      unknown: 0,
    },
    median_hire_time_days: 0,
    offer_acceptance_rate: 0,
    offer_statuses: {
      hired: 0,
      rescinded: 0,
      declined: 0,
    },
    sources: {
      inbound: 0,
      outbound: 0,
      internal: 0,
      referral: 0,
      other: 0,
    },
    total_hires: 0,
  });
  const error = ref(null);
  const flags = ref({});

  const isEmptyData = computed(() => isEmpty(data.value)
    || Object.values(data.value)
      .every((item) => Object.values(item || {}).every((value) => value === 0)));

  const tableRows = computed(() => {
    const {
      offer_statuses: offerStatuses, current_open_offers: currentOpenOffers,
    } = data.value;

    const resolvedStatuses = offerStatuses.hired + offerStatuses.rescinded + offerStatuses.declined;

    const resolvedRate = resolvedStatuses > 0
      ? `${roundFloat((resolvedStatuses / resolvedStatuses) * 100)}%`
      : 0;
    const acceptedRate = resolvedStatuses > 0
      ? `${roundFloat((offerStatuses.hired / resolvedStatuses) * 100)}%`
      : 0;
    const declinedRate = resolvedStatuses > 0
      ? `${roundFloat((offerStatuses.declined / resolvedStatuses) * 100)}%`
      : 0;
    const rescindedRate = resolvedStatuses > 0
      ? `${roundFloat((offerStatuses.rescinded / resolvedStatuses) * 100)}%`
      : 0;

    const rows = [
      {
        label: 'Count',
        resolved: {
          value: resolvedStatuses || '-',
          showArrow: true,
        },
        accepted: {
          value: offerStatuses.hired || '-',
        },
        declined: {
          value: offerStatuses.declined || '-',
        },
        rescinded: {
          value: offerStatuses.rescinded || '-',
          showArrow: true,
        },
        currentOpen: {
          value: currentOpenOffers ? numberWithCommas(currentOpenOffers) : '-',
        },
      },
      {
        label: 'Rate',
        resolved: {
          value: resolvedRate,
          showArrow: true,
        },
        accepted: {
          value: acceptedRate,
        },
        declined: {
          value: declinedRate,
        },
        rescinded: {
          value: rescindedRate,
          showArrow: true,
        },
        currentOpen: {
          value: null,
        },
      },
    ];

    return rows;
  });

  const calculateCandidatesForSummary = () => {
    const {
      hired = 0,
      rescinded = 0,
      declined = 0,
    } = data.value?.offer_statuses || {};

    return numberWithCommas(hired + rescinded + declined || 0);
  };

  const loadHiringReport = async ({
    selectedDateRange,
    routeQuery,
    selectedFilters,
    selectedMyJobs,
  }) => {
    error.value = null;

    const lastQuarter = getQuarter(1);
    const fallbackDateRange = selectedDateRange || { from: format(lastQuarter[0], 'yyyy-MM-dd'), to: format(lastQuarter[1], 'yyyy-MM-dd') };
    const date = routeQuery?.from && routeQuery?.to
      ? { from: routeQuery.from, to: routeQuery.to }
      : fallbackDateRange;

    useAppLoading().setLoading({
      pageLoading: true,
    });

    try {
      const { data: responseData, flags: responseFlags } = await fetchHiringReport({
        selectedDateRange: date, routeQuery, filters: selectedFilters, owned: selectedMyJobs,
      });

      data.value = responseData;
      flags.value = responseFlags;

      if (responseData?.offer_statuses) {
        const total = calculateCandidatesForSummary();
        useDatasetCounts().setDatasetCounts(total);
      }
    } catch (err) {
      useAppSnackbar().addErrorSnackbar({
        text: `Unable to load hiring report - ${err.message}`,
        timeout: 3500,
      });
      error.value = err;
      throw err;
    } finally {
      useAppLoading().setLoading({
        pageLoading: false,
      });
    }
  };

  const downloadHiringCSV = ({ dateRange, projectName }) => {
    assignInsightsTableToCSVDownloadLink({
      tableRows: tableRows.value,
      tableHeaders: [
        'Resolved',
        'Accepted',
        'Declined',
        'Rescinded',
        'Current Open',
      ],
      dateRange,
      projectName,
      page: 'hiring-report',
    });
  };

  return {
    // State
    data,
    flags,
    error,
    tableRows,

    // Computed
    isEmptyData,

    // Actions
    loadHiringReport,
    downloadHiringCSV,
  };
});
